import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

// Route to breadcrumb label mapping
const ROUTE_LABELS = {
  '/dashboard': 'Dashboard',
  '/dashboard-executivo': 'Dashboard Executivo',
  '/instituicoes': 'Instituições',
  '/utentes': 'Utentes',
  '/avaliacoes': 'Avaliações',
  '/recursos': 'Recursos',
  '/priorizacao': 'Priorização IA',
  '/voluntariado': 'Voluntariado',
  '/matching-analytics': 'Analytics',
  '/campanhas': 'Campanhas',
  '/sendys': 'Sendys CRM',
  '/relatorios': 'Relatórios',
  '/documentos': 'Documentos',
  '/rgpd': 'RGPD',
  '/admin-portal': 'Portal Instituições',
  '/alertas': 'Alertas',
  '/publico': 'Portal Público',
  '/publico/voluntariado': 'Voluntariado',
  '/publico/dados-abertos': 'Dados Abertos',
  '/publico/instituicoes': 'Instituições',
  '/portal': 'Portal',
  '/portal/dashboard': 'Dashboard',
  '/portal/instituicao': 'Minha Instituição',
  '/portal/valencias': 'Respostas Sociais',
  '/portal/pedidos': 'Pedidos',
  '/portal/voluntariado': 'Voluntariado',
  '/portal/mensagens': 'Mensagens',
  '/portal/alterar-password': 'Alterar Password',
};

export default function Breadcrumbs({ customItems }) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Build breadcrumb items from path
  const breadcrumbs = [];
  let currentPath = '';

  // Always start with Home
  breadcrumbs.push({
    label: 'Início',
    path: '/dashboard',
    icon: Home
  });

  // Add intermediate segments
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Skip if it's a UUID or numeric ID
    if (segment.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      // This is an ID - try to get a better label
      breadcrumbs.push({
        label: `#${segment.slice(0, 8)}...`,
        path: currentPath,
        isId: true
      });
    } else if (segment.match(/^\d+$/)) {
      // Numeric ID
      breadcrumbs.push({
        label: `#${segment}`,
        path: currentPath,
        isId: true
      });
    } else {
      const label = ROUTE_LABELS[currentPath] || 
                    segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
      
      breadcrumbs.push({
        label,
        path: currentPath
      });
    }
  });

  // Override with custom items if provided
  const finalItems = customItems || breadcrumbs;

  // Don't show breadcrumbs if we're at root or only have home
  if (finalItems.length <= 1) {
    return null;
  }

  return (
    <nav 
      aria-label="Breadcrumb" 
      className="flex items-center gap-1 text-sm mb-4"
      data-testid="breadcrumbs"
    >
      {finalItems.map((item, index) => {
        const isLast = index === finalItems.length - 1;
        const Icon = item.icon;
        
        return (
          <React.Fragment key={item.path}>
            {index > 0 && (
              <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
            )}
            
            {isLast ? (
              <span 
                className="font-medium text-slate-900 dark:text-slate-100 truncate max-w-[200px]"
                title={item.label}
              >
                {Icon && <Icon className="w-4 h-4 inline mr-1" />}
                {item.label}
              </span>
            ) : (
              <Link
                to={item.path}
                className="text-slate-500 hover:text-[#0066CC] dark:text-slate-400 dark:hover:text-[#0066CC] transition-colors truncate max-w-[150px] flex items-center"
                title={item.label}
              >
                {Icon && <Icon className="w-4 h-4 mr-1 flex-shrink-0" />}
                <span className="truncate">{item.label}</span>
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

// Helper hook to set custom breadcrumb items
export function useBreadcrumbs(items) {
  // This could be extended to use context for more complex scenarios
  return items;
}
